<script setup lang="ts">
import { computed } from 'vue'
import { TelegramIcon, MailIcon, PhoneIcon } from '@/components/icons'
import { showToast } from '@/utils/toast'

interface Props {
  type: 'telegram' | 'mail' | 'phone'
  href: string
  text?: string
  iconWidth?: number | string
  iconHeight?: number | string
  email?: string
  telegram?: string
  phone?: string
}

const {
  type,
  href,
  text,
  iconWidth = 24,
  iconHeight = 24,
  email,
  telegram,
  phone,
} = defineProps<Props>()

const displayText = text || (type === 'telegram' ? 'TELEGRAM' : type === 'phone' ? 'PHONE' : 'MAIL')

const linkTarget = computed(() => {
  if ((type === 'mail' && email) || (type === 'phone' && phone)) {
    return undefined
  }
  return '_blank'
})

const linkRel = computed(() => {
  if ((type === 'mail' && email) || (type === 'phone' && phone)) {
    return undefined
  }
  return 'noopener noreferrer'
})

const handleClick = (event: MouseEvent) => {
  if (type === 'mail' && email) {
    event.preventDefault()
    // Копируем email в буфер обмена
    navigator.clipboard
      .writeText(email)
      .then(() => {
        // Показываем красивое уведомление
        showToast('Адрес скопирован')
      })
      .catch((err) => {
        console.error('Ошибка при копировании:', err)
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea')
        textArea.value = email
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          showToast('Адрес скопирован')
        } catch (err) {
          console.error('Ошибка при копировании:', err)
        }
        document.body.removeChild(textArea)
      })
  } else if (type === 'phone' && phone) {
    event.preventDefault()
    // Копируем телефон в буфер обмена
    navigator.clipboard
      .writeText(phone)
      .then(() => {
        // Показываем красивое уведомление
        showToast('Номер скопирован')
      })
      .catch((err) => {
        console.error('Ошибка при копировании:', err)
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea')
        textArea.value = phone
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          showToast('Номер скопирован')
        } catch (err) {
          console.error('Ошибка при копировании:', err)
        }
        document.body.removeChild(textArea)
      })
  }
}
</script>

<template>
  <a
    class="contact-button"
    :class="{
      'contact-button--with-email': type === 'mail' && email,
      'contact-button--with-telegram': type === 'telegram' && telegram,
      'contact-button--with-phone': type === 'phone' && phone,
    }"
    :href="(type === 'mail' && email) || (type === 'phone' && phone) ? '#' : href"
    v-bind="{
      ...(linkTarget ? { target: linkTarget } : {}),
      ...(linkRel ? { rel: linkRel } : {}),
    }"
    @click="handleClick"
  >
    <TelegramIcon v-if="type === 'telegram'" :width="iconWidth" :height="iconHeight" />
    <PhoneIcon v-else-if="type === 'phone'" :width="iconWidth" :height="iconHeight" />
    <MailIcon v-else :width="iconWidth" :height="iconHeight" />
    <span class="contact-button__text-wrapper">
      <span class="contact-button__text contact-button__text--default">
        {{ displayText }}
      </span>
      <span
        v-if="type === 'mail' && email"
        class="contact-button__text contact-button__text--email"
      >
        {{ email }}
      </span>
      <span
        v-if="type === 'telegram' && telegram"
        class="contact-button__text contact-button__text--telegram"
      >
        {{ telegram }}
      </span>
      <span
        v-if="type === 'phone' && phone"
        class="contact-button__text contact-button__text--phone"
      >
        {{ phone }}
      </span>
    </span>
  </a>
</template>

<style scoped>
.contact-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0;
  border-radius: 999px;
  text-decoration: none;
  color: inherit;
  background-color: var(--color-white);
  cursor: pointer;
}

.contact-button__text-wrapper {
  position: relative;
  display: inline-block;
}

.contact-button__text {
  font-size: 0.875rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  padding-bottom: 0.125rem;
  transition: opacity 0.3s ease-in-out;
  display: inline-block;
}

/* Для кнопки с email/telegram/phone - меняем текст при наведении */
.contact-button--with-email .contact-button__text-wrapper,
.contact-button--with-telegram .contact-button__text-wrapper,
.contact-button--with-phone .contact-button__text-wrapper {
  position: relative;
}

.contact-button--with-email .contact-button__text--default,
.contact-button--with-telegram .contact-button__text--default,
.contact-button--with-phone .contact-button__text--default {
  opacity: 1;
}

.contact-button--with-email .contact-button__text--email,
.contact-button--with-telegram .contact-button__text--telegram,
.contact-button--with-phone .contact-button__text--phone {
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  text-transform: none;
  pointer-events: none;
}

.contact-button--with-email:hover .contact-button__text--default,
.contact-button--with-telegram:hover .contact-button__text--default,
.contact-button--with-phone:hover .contact-button__text--default {
  opacity: 0;
}

.contact-button--with-email:hover .contact-button__text--email,
.contact-button--with-telegram:hover .contact-button__text--telegram,
.contact-button--with-phone:hover .contact-button__text--phone {
  opacity: 1;
}
</style>
